import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { RefreshCcw, XCircle, Terminal, BarChart3 } from 'lucide-react';
import { CodeAgentTurnMetricsDialog } from './CodeAgentTurnMetricsDialog';
import { useCodeAgentSessions, useKillSession } from '../../services/code-agent.service';
import { EmptyState } from '../../components/admin/EmptyState';
import { formatDateTime } from '../../utils/dateFormat';

const getStatusColor = (status: string) => {
  switch (status) {
    case 'active': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    case 'paused': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
    case 'error': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
    case 'completed': return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
    default: return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
  }
};

export const CodeAgentSessionsPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState<string>('');
  
  const { data: response, isLoading, isFetching, error, refetch } = useCodeAgentSessions(page + 1, pageSize, statusFilter || undefined);
  const killMutation = useKillSession();

  const [killConfirmId, setKillConfirmId] = useState<string | null>(null);
  const [metricsSessionId, setMetricsSessionId] = useState<string | null>(null);

  const sessions = response?.data || [];
  const totalCount = response?.meta?.total || sessions.length;

  const handleKill = async () => {
    if (killConfirmId) {
      try {
        await killMutation.mutateAsync(killConfirmId);
        setKillConfirmId(null);
      } catch (e) {}
    }
  };

  const columns: GridColDef[] = [
    { field: 'publicId', headerName: 'ID', width: 130 },
    { 
      field: 'workspace', 
      headerName: 'Workspace', 
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2" className="truncate text-gray-900 dark:text-slate-100">
          {params.row.conversation?.workspace?.fingerprint || 'Unknown'}
        </Typography>
      )
    },
    { 
      field: 'conversation', 
      headerName: 'Conversation Title', 
      flex: 1.5,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2" className="truncate text-gray-900 dark:text-slate-100">
          {params.row.conversation?.title || 'Untitled'}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip size="small" label={params.value.toUpperCase()} className={`font-semibold ${getStatusColor(params.value)}`} />
      ),
    },
    {
      field: 'activeTurnCount',
      headerName: 'Active Turns',
      width: 120,
      valueGetter: (_value, row) => row.codeAgent?.activeTurnCount ?? 0,
      renderCell: (params) => (
        <Chip
          size="small"
          label={`${params.value}/${params.row.codeAgent?.turnCount ?? 0}`}
          color={Number(params.value) > 0 ? 'primary' : 'default'}
          variant={Number(params.value) > 0 ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'openActionCount',
      headerName: 'Open Actions',
      width: 125,
      valueGetter: (_value, row) => row.codeAgent?.openActionCount ?? 0,
      renderCell: (params) => (
        <Chip
          size="small"
          label={`${params.value}/${params.row.codeAgent?.actionCount ?? 0}`}
          color={Number(params.value) > 0 ? 'warning' : 'default'}
          variant={Number(params.value) > 0 ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'unknownActionCount',
      headerName: 'Unknown',
      width: 105,
      valueGetter: (_value, row) => row.codeAgent?.unknownActionCount ?? 0,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value}
          color={Number(params.value) > 0 ? 'error' : 'default'}
          variant={Number(params.value) > 0 ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'openRecoveryIncidentCount',
      headerName: 'Recovery',
      width: 110,
      valueGetter: (_value, row) => row.codeAgent?.openRecoveryIncidentCount ?? 0,
      renderCell: (params) => (
        <Chip
          size="small"
          label={`${params.value}/${params.row.codeAgent?.recoveryIncidentCount ?? 0}`}
          color={Number(params.value) > 0 ? 'warning' : 'default'}
          variant={Number(params.value) > 0 ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 160,
      renderCell: (params) => (
        <Typography variant="body2">
          {formatDateTime(params.value)}
        </Typography>
      )
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      width: 160,
      renderCell: (params) => (
        <Typography variant="body2">
          {formatDateTime(params.value)}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      renderCell: (params) => (
        <Box className="flex w-full justify-end gap-1 items-center h-full">
          <IconButton
            size="small"
            onClick={() => setMetricsSessionId(params.row.publicId)}
            className="text-gray-600 hover:bg-gray-100 dark:text-slate-300 dark:hover:bg-slate-700"
            title="Turn metrics (tokens, cache, retries)"
          >
            <BarChart3 size={18} />
          </IconButton>
          {(params.row.status === 'active' || params.row.status === 'paused') && (
            <IconButton 
              size="small" 
              onClick={() => setKillConfirmId(params.row.publicId)}
              className="text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
              title="Kill Session"
            >
              <XCircle size={18} />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box className="min-h-screen bg-slate-50 p-6 dark:bg-slate-900">
      {/* Header */}
      <Box className="mb-6 flex justify-between items-center">
        <Box>
          <Typography variant="h4" className="mb-2 font-bold text-gray-900 dark:text-slate-100">
            Code Agent Sessions
          </Typography>
          <Typography variant="body2" className="text-gray-600 dark:text-slate-400">
            Monitor and manage active Code Agent sessions across all workspaces.
          </Typography>
        </Box>
        <Box className="flex gap-3 items-center">
          <FormControl size="small" className="w-48 bg-white dark:bg-slate-800">
            <InputLabel>Filter Status</InputLabel>
            <Select
              value={statusFilter}
              label="Filter Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="paused">Paused</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="error">Error</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<RefreshCcw size={16} />}
            onClick={() => refetch()}
            className="text-gray-700 border-gray-300 hover:bg-gray-100 dark:text-slate-300 dark:border-slate-600 dark:hover:bg-slate-800 h-10"
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Table Card */}
      <Box className="rounded-xl bg-white shadow-sm dark:bg-slate-800">
        <Box className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-slate-700">
          <Typography variant="h6" className="font-semibold text-gray-900 dark:text-slate-100">
            Sessions
          </Typography>
        </Box>

        {isLoading ? (
          <Box className="flex items-center justify-center p-12">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box className="p-12">
            <Typography className="text-center text-red-600 dark:text-red-400">
              Failed to load sessions. Please try again.
            </Typography>
          </Box>
        ) : sessions.length > 0 ? (
          <DataGrid
            rows={sessions}
            columns={columns}
            getRowId={(row) => row.publicId}
            loading={isFetching}
            disableRowSelectionOnClick
            paginationMode="server"
            rowCount={totalCount}
            pageSizeOptions={[10, 20, 50]}
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(model) => {
              setPage(model.page);
              setPageSize(model.pageSize);
            }}
            autoHeight
            className="border-0"
            sx={{
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid',
                borderColor: 'divider',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'background.default',
                borderBottom: '2px solid',
                borderColor: 'divider',
              },
            }}
          />
        ) : (
          <Box className="p-12">
            <EmptyState
              Icon={Terminal}
              title="No Sessions Found"
              description="No code agent sessions match the current filters."
            />
          </Box>
        )}
      </Box>

      <CodeAgentTurnMetricsDialog
        sessionPublicId={metricsSessionId}
        onClose={() => setMetricsSessionId(null)}
      />

      {/* Kill Confirmation Dialog */}
      <Dialog
        open={killConfirmId !== null}
        onClose={() => setKillConfirmId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle className="bg-red-50 dark:bg-red-900/20">
          <Typography variant="h6" className="font-semibold text-red-900 dark:text-red-400">
            Confirm Kill Session
          </Typography>
        </DialogTitle>
        <DialogContent className="mt-4">
          <Typography className="text-gray-700 dark:text-slate-300">
            Are you sure you want to kill this session? This will immediately terminate any running agent processes for this conversation.
          </Typography>
        </DialogContent>
        <DialogActions className="px-6 pb-4">
          <Button
            onClick={() => setKillConfirmId(null)}
            disabled={killMutation.isPending}
            className="text-gray-600 dark:text-slate-400"
          >
            Cancel
          </Button>
          <Button
            onClick={handleKill}
            variant="contained"
            disabled={killMutation.isPending}
            className="bg-red-600 text-white hover:bg-red-700"
            startIcon={
              killMutation.isPending ? <CircularProgress size={16} /> : <XCircle size={16} />
            }
          >
            {killMutation.isPending ? 'Killing...' : 'Kill Session'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
